/**
 * Executive Dashboard — pure calculations behind the cards. No React, no I/O,
 * so every number the page derives is unit-tested (executiveDashboardLogic.test.ts).
 * The raw counts come from AI_Dashboard_Kpis_SP; each one's unit is stated in
 * {@link Kpis} and rendered next to the number on the page.
 */

/** Headline KPIs — mirrors the API's LabDashboardKpis (camelCase). */
export interface Kpis {
  // Job-level — unit: jobs (open = not fully reported; delivered = reports)
  openJobs: number; overdue: number; dueToday: number;
  totalJobs30d: number; reportsDelivered7d: number; reportsDeliveredOnTime7d: number;
  samplesDue3dTests: number; samplesDue3dSamples: number; samplesDue3dClients: number;
  // Job Status donut — each open job in exactly ONE bucket; sum = openJobs
  bucketOverdue: number; bucketDueToday: number; bucketInReporting: number; bucketInProgress: number;
  // Pipeline stages
  pendingSubmission: number;      // jobs
  samplePrepBacklog: number;      // tests
  pendingValidation: number;      // tests
  pendingResultApproval: number;  // tests
  pendingReporting: number;       // jobs
  // SDMS — unit: batches
  sdmsPendingReview: number; sdmsValidated: number;
  // Totals
  totalSamples: number; totalTests: number;
  totalClients: number; activeClients: number; activeProjects: number; qcBatches: number;
}

export interface DailyCount { date: string; count: number }
export interface TopClient { name: string; count: number; totalJobs: number }

export type Unit = 'jobs' | 'tests' | 'batches';

export const fmt = (n: number | undefined | null) => (n == null ? '—' : n.toLocaleString());

/**
 * Whole-number percentages of `parts` that always add up to exactly 100
 * (largest-remainder rounding). All zeros → all zeros.
 */
export function percentages(parts: number[]): number[] {
  const total = parts.reduce((s, v) => s + Math.max(v, 0), 0);
  if (total <= 0) return parts.map(() => 0);
  const raw = parts.map(v => (Math.max(v, 0) / total) * 100);
  const floored = raw.map(Math.floor);
  let left = 100 - floored.reduce((s, v) => s + v, 0);
  const order = raw
    .map((v, i) => ({ i, rem: v - Math.floor(v) }))
    .sort((a, b) => b.rem - a.rem);
  for (const { i } of order) {
    if (left <= 0) break;
    floored[i] = (floored[i] ?? 0) + 1;
    left -= 1;
  }
  return floored;
}

export interface DonutSegment { label: string; val: number; pct: number; color: string }

/**
 * Job Status donut. The four buckets come from the SQL already mutually
 * exclusive (Overdue → Due Today → In Reporting → In Progress), so the slices
 * add up to the open-job total and can never go negative.
 */
export function buildDonut(k: Kpis): { gradient: string; segments: DonutSegment[]; total: number } {
  const defs = [
    { label: 'In Reporting', val: k.bucketInReporting, color: '#5DCAA5' },
    { label: 'In Progress',  val: k.bucketInProgress,  color: '#f5c518' },
    { label: 'Overdue',      val: k.bucketOverdue,     color: '#ff7d6e' },
    { label: 'Due Today',    val: k.bucketDueToday,    color: '#cccccc' },
  ].map(d => ({ ...d, val: Math.max(d.val ?? 0, 0) }));
  const total = defs.reduce((s, d) => s + d.val, 0);
  const pcts = percentages(defs.map(d => d.val));

  if (total === 0) {
    return { gradient: 'conic-gradient(#ddd 0% 100%)', total, segments: defs.map((d, i) => ({ ...d, pct: pcts[i] ?? 0 })) };
  }
  // Exact (unrounded) angles for the ring; rounded values only for the legend.
  let acc = 0;
  const stops = defs.map(d => {
    const from = (acc / total) * 100;
    acc += d.val;
    const to = (acc / total) * 100;
    return `${d.color} ${from.toFixed(2)}% ${to.toFixed(2)}%`;
  });
  return {
    gradient: `conic-gradient(${stops.join(', ')})`,
    total,
    segments: defs.map((d, i) => ({ ...d, pct: pcts[i] ?? 0 })),
  };
}

/**
 * Jobs received in the last 7 days minus the 7 days before. The series is
 * zero-filled by the SQL (one row per calendar day, last row = today), so
 * slicing by position is slicing by calendar day. Needs 14 days; else null.
 */
export function weekOverWeek(trend: DailyCount[]): number | null {
  if (trend.length < 14) return null;
  const sum = (xs: DailyCount[]) => xs.reduce((s, d) => s + d.count, 0);
  return sum(trend.slice(-7)) - sum(trend.slice(-14, -7));
}

/** Axis label `MM/DD` from an ISO-ish date string (chart-axis tick, §18.6 exception). */
export function axisLabel(date: string | undefined): string {
  if (!date || date.length < 10) return '';
  return `${date.slice(5, 7)}/${date.slice(8, 10)}`;
}

/** Points for the trend sparkline in a 400 × 70 viewBox. */
export function sparkPoints(data: DailyCount[]): { x: number; y: number }[] {
  if (data.length < 2) return [];
  const vals = data.map(d => d.count);
  const maxV = Math.max(...vals, 1);
  const minV = Math.min(...vals);
  const range = maxV - minV || 1;
  const n = data.length;
  return data.map((d, i) => ({ x: (i / (n - 1)) * 400, y: 10 + ((maxV - d.count) / range) * 50 }));
}

/** A client's share of ALL jobs received in the window (not just the top N). */
export function clientShare(c: TopClient): number {
  return c.totalJobs > 0 ? Math.round((c.count / c.totalJobs) * 100) : 0;
}

/** On-time delivery rate of the last 7 days, or null when nothing was delivered. */
export function onTimePct(k: Kpis): number | null {
  if (!k.reportsDelivered7d) return null;
  return Math.round((k.reportsDeliveredOnTime7d / k.reportsDelivered7d) * 100);
}

export interface StageRow {
  key: string;
  label: string;
  count: number;
  unit: Unit;
  color: string;
  /** Drill-down detail endpoint, when one exists. */
  drill?: 'due-today' | 'pending-validation' | 'pending-approval' | 'pending-reporting';
}

/** Backlog pipeline, in workflow order. Every row carries its unit. */
export function pipelineStages(k: Kpis): StageRow[] {
  return [
    { key: 'due',   label: '00 — Due Today',          count: k.dueToday,              unit: 'jobs',    color: '#e8a014', drill: 'due-today' },
    { key: 'sub',   label: '01 — Pending Submission', count: k.pendingSubmission,     unit: 'jobs',    color: '#e8a014' },
    { key: 'prep',  label: '02 — Sample Prep Backlog',count: k.samplePrepBacklog,     unit: 'tests',   color: '#185FA5' },
    { key: 'sdms',  label: '03 — SDMS Result Entry',  count: k.sdmsPendingReview,     unit: 'batches', color: '#185FA5' },
    { key: 'val',   label: '04 — Data Validation',    count: k.pendingValidation,     unit: 'tests',   color: '#534AB7', drill: 'pending-validation' },
    { key: 'appr',  label: '05 — Result Approval',    count: k.pendingResultApproval, unit: 'tests',   color: '#534AB7', drill: 'pending-approval' },
    { key: 'rep',   label: '06 — Reporting',          count: k.pendingReporting,      unit: 'jobs',    color: '#0F6E56', drill: 'pending-reporting' },
  ];
}

/**
 * Bar width (0–100) for each row, relative to the largest count OF THE SAME
 * UNIT — a bar never compares tests with jobs or batches.
 */
export function barWidths(rows: { count: number; unit: Unit }[]): number[] {
  const maxByUnit = new Map<Unit, number>();
  for (const r of rows) maxByUnit.set(r.unit, Math.max(maxByUnit.get(r.unit) ?? 0, r.count));
  return rows.map(r => {
    const max = maxByUnit.get(r.unit) ?? 0;
    return max > 0 ? Math.round((Math.max(r.count, 0) / max) * 100) : 0;
  });
}

export interface Alert { kind: '' | 'warn' | 'info' | 'ok'; icon: string; title: string; sub: string }

const plural = (n: number, one: string, many: string) => `${fmt(n)} ${n === 1 ? one : many}`;

/** Alerts & Exceptions — every message names the real unit it counts. */
export function buildAlerts(k: Kpis): Alert[] {
  const alerts: Alert[] = [];
  if (k.overdue > 0) {
    alerts.push({ kind: '', icon: 'ti-alert-octagon',
      title: `${plural(k.overdue, 'job', 'jobs')} overdue`,
      sub: 'Not fully reported and past the due date' });
  }
  if (k.dueToday > 0) {
    alerts.push({ kind: 'warn', icon: 'ti-alert-triangle',
      title: `${plural(k.dueToday, 'job', 'jobs')} due today`,
      sub: 'Not fully reported yet' });
  }
  if (k.sdmsPendingReview > 0) {
    alerts.push({ kind: 'warn', icon: 'ti-clock-exclamation',
      title: `${plural(k.sdmsPendingReview, 'SDMS batch', 'SDMS batches')} awaiting submission / validation`,
      sub: 'SDMS Result Entry queue' });
  }
  if (k.pendingResultApproval > 0) {
    alerts.push({ kind: 'info', icon: 'ti-info-circle',
      title: `${plural(k.pendingResultApproval, 'test', 'tests')} pending result approval`,
      sub: 'Reviewer sign-off required' });
  }
  if (alerts.length === 0) {
    alerts.push({ kind: 'ok', icon: 'ti-circle-check', title: 'All clear', sub: 'No overdue jobs or pending queues' });
  }
  return alerts;
}

/** Number of alerts that need action (errors + warnings). */
export const activeAlertCount = (alerts: Alert[]) => alerts.filter(a => a.kind === '' || a.kind === 'warn').length;
